"use client"

import Link from "next/link"
import { useState, useRef, useEffect } from "react"
import { useI18n } from "@/lib/i18n/context"
import {
  Menu,
  X,
  ArrowRight,
  ChevronDown,
  Receipt,
  CalendarClock,
  Wallet,
  BarChart3,
  Factory,
  Warehouse,
  ShoppingCart,
  Fingerprint,
  Palmtree,
  Users,
  MessageSquareText,
  FileText,
  Bean,
} from "lucide-react"
import { cn } from "@/lib/utils"

const businessFeatures = [
  {
    name: "Facturación",
    description: "Compras, ventas y contabilidad",
    href: "/facturacion",
    icon: Receipt,
    color: "bg-lime-50 text-lime-600",
  },
  {
    name: "Horarios y Turnos",
    description: "Planificación de equipos",
    href: "/horarios",
    icon: CalendarClock,
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    name: "Nóminas",
    description: "Gestión de nóminas automática",
    href: "/nominas",
    icon: Wallet,
    color: "bg-amber-50 text-amber-600",
  },
  {
    name: "Reporting",
    description: "Informes y analíticas",
    href: "/reporting",
    icon: BarChart3,
    color: "bg-sky-50 text-sky-600",
  },
  {
    name: "Producción",
    description: "Recetas y elaboraciones",
    href: "/produccion",
    icon: Factory,
    color: "bg-orange-50 text-orange-600",
  },
  {
    name: "Inventario",
    description: "Control de stock",
    href: "/inventario",
    icon: Warehouse,
    color: "bg-violet-50 text-violet-600",
  },
  {
    name: "Compras",
    description: "Gestión de proveedores",
    href: "/compras",
    icon: ShoppingCart,
    color: "bg-orange-50 text-orange-600",
  },
]

const employeeFeatures = [
  {
    name: "Control de Asistencia",
    description: "Fichaje digital y geolocalización",
    href: "/empleados#fichaje",
    icon: Fingerprint,
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    name: "Vacaciones",
    description: "Solicitudes y saldos",
    href: "/empleados#vacaciones",
    icon: Palmtree,
    color: "bg-amber-50 text-amber-600",
  },
  {
    name: "Calendario de Equipo",
    description: "Ausencias del departamento",
    href: "/empleados#calendario",
    icon: Users,
    color: "bg-rose-50 text-rose-600",
  },
  {
    name: "Buzón de Sugerencias",
    description: "Comunicación con RRHH",
    href: "/empleados#sugerencias",
    icon: MessageSquareText,
    color: "bg-violet-50 text-violet-600",
  },
  {
    name: "Documentos",
    description: "Contratos y certificados",
    href: "/empleados#documentos",
    icon: FileText,
    color: "bg-cyan-50 text-cyan-600",
  },
]

// Las paginas que hablan a un tipo de negocio entero, no de una funcion suelta. Van
// debajo de la columna del empleado, que es la corta: ocupan el hueco que ya habia.
const sectorFeatures = [
  {
    name: "Tostadores de café",
    description: "Del grano verde a tu tienda de Shopify",
    href: "/tostadores",
    icon: Bean,
    color: "bg-zinc-100 text-zinc-700",
  },
]

export default function PublicHeader({ isHome }: { isHome?: boolean }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const scrollTo = (id: string) => {
    setOpen(false)
    if (isHome) {
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: "smooth" })
      }
    }
  }

  const getHref = (id: string) => (isHome ? `#${id}` : `/#${id}`)

  const handleFeaturesOverviewClick = () => {
    setDropdownOpen(false)
    scrollTo("funcionalidades")
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setDropdownOpen(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setDropdownOpen(false)
    }, 150)
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-zinc-200/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-3">
          <img
            src="/zindra_black.svg"
            alt="Zindra"
            className="w-auto"
            style={{ height: "calc(var(--spacing) * 9)" }}
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex gap-x-10 items-center">
          {/* Funcionalidades Dropdown */}
          <div
            ref={dropdownRef}
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={cn(
                "flex items-center gap-1 text-sm font-medium transition-colors",
                dropdownOpen ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-900"
              )}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              Funcionalidades
              <ChevronDown
                className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  dropdownOpen && "rotate-180"
                )}
              />
            </button>

            {/* Dropdown Panel */}
            <div
              className={cn(
                "absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-200",
                dropdownOpen
                  ? "opacity-100 translate-y-0 pointer-events-auto"
                  : "opacity-0 -translate-y-2 pointer-events-none"
              )}
            >
              <div className="bg-white rounded-2xl shadow-xl border border-zinc-200 p-6 w-[640px]">
                <div className="grid grid-cols-2 gap-6">
                  {/* Business Column */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-4 px-2">
                      Para tu Negocio
                    </h3>
                    <div className="space-y-1">
                      {businessFeatures.map((feature) => (
                        <Link
                          key={feature.name}
                          href={feature.href}
                          className="flex items-start gap-3 p-2 rounded-xl hover:bg-zinc-50 transition-colors group"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", feature.color)}>
                            <feature.icon className="w-5 h-5" />
                          </div>
                          <div className="pt-0.5">
                            <div className="text-sm font-medium text-zinc-900 group-hover:text-lime-600 transition-colors">
                              {feature.name}
                            </div>
                            <div className="text-xs text-zinc-500">{feature.description}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Employee Column */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-4 px-2">
                      Portal del Empleado
                    </h3>
                    <div className="space-y-1">
                      {employeeFeatures.map((feature) => (
                        <Link
                          key={feature.name}
                          href={feature.href}
                          className="flex items-start gap-3 p-2 rounded-xl hover:bg-zinc-50 transition-colors group"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", feature.color)}>
                            <feature.icon className="w-5 h-5" />
                          </div>
                          <div className="pt-0.5">
                            <div className="text-sm font-medium text-zinc-900 group-hover:text-emerald-600 transition-colors">
                              {feature.name}
                            </div>
                            <div className="text-xs text-zinc-500">{feature.description}</div>
                          </div>
                        </Link>
                      ))}
                    </div>

                    <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mt-5 mb-4 px-2">
                      Por tipo de negocio
                    </h3>
                    <div className="space-y-1">
                      {sectorFeatures.map((feature) => (
                        <Link
                          key={feature.name}
                          href={feature.href}
                          className="flex items-start gap-3 p-2 rounded-xl hover:bg-zinc-50 transition-colors group"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", feature.color)}>
                            <feature.icon className="w-5 h-5" />
                          </div>
                          <div className="pt-0.5">
                            <div className="text-sm font-medium text-zinc-900 group-hover:underline underline-offset-4">
                              {feature.name}
                            </div>
                            <div className="text-xs text-zinc-500">{feature.description}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bottom CTA */}
                <div className="mt-6 pt-4 border-t border-zinc-100">
                  <Link
                    href={getHref("funcionalidades")}
                    className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-emerald-50 to-lime-50 hover:from-emerald-100 hover:to-lime-100 transition-colors group"
                    onClick={handleFeaturesOverviewClick}
                  >
                    <div>
                      <div className="text-sm font-medium text-zinc-900">Ver todas las funcionalidades</div>
                      <div className="text-xs text-zinc-500">Descubre cómo Zindra puede ayudar a tu negocio</div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-zinc-400 group-hover:text-zinc-900 group-hover:translate-x-1 transition-all" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <Link
            href="/empleados"
            className="hover:text-zinc-900 transition-colors text-sm font-medium text-zinc-500"
          >
            Empleados
          </Link>
          <Link
            href="/restaurantes"
            className="hover:text-zinc-900 transition-colors text-sm font-medium text-zinc-500"
          >
            Guía de restaurantes
          </Link>
          <Link
            href="/precios"
            className="hover:text-zinc-900 transition-colors text-sm font-medium text-zinc-500"
          >
            Precios
          </Link>
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex gap-x-6 items-center">
          <Link
            href="/login"
            className="text-sm font-semibold text-zinc-900 hover:underline underline-offset-4 transition-colors"
          >
            Iniciar Sesión
          </Link>
          <Link
            href="/register"
            className="text-primary-foreground transition-all font-medium px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 group bg-primary hover:bg-[var(--primary-hover)]"
          >
            Comenzar Gratis
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2 rounded-lg hover:bg-zinc-100 transition-colors"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={cn(
          "lg:hidden overflow-hidden transition-all duration-300 ease-in-out border-t border-zinc-200/50",
          open ? "max-h-[80vh] opacity-100 overflow-y-auto" : "max-h-0 opacity-0 border-t-0"
        )}
      >
        <div className="glass-panel px-6 py-4">
          <nav className="flex flex-col gap-1">
            {/* Mobile Funcionalidades Accordion */}
            <button
              className="flex items-center justify-between px-4 py-3 text-sm font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors w-full"
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
            >
              <span>Funcionalidades</span>
              <ChevronDown
                className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  mobileDropdownOpen && "rotate-180"
                )}
              />
            </button>

            {/* Mobile Dropdown Content */}
            <div
              className={cn(
                "overflow-hidden transition-all duration-300",
                mobileDropdownOpen ? "max-h-[640px] opacity-100" : "max-h-0 opacity-0"
              )}
            >
              <div className="pl-4 pr-2 py-2 space-y-4">
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2 px-2">
                    Para tu Negocio
                  </h4>
                  <div className="space-y-1">
                    {businessFeatures.slice(0, 4).map((feature) => (
                      <Link
                        key={feature.name}
                        href={feature.href}
                        className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-zinc-100 transition-colors"
                        onClick={() => {
                          setOpen(false)
                          setMobileDropdownOpen(false)
                        }}
                      >
                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", feature.color)}>
                          <feature.icon className="w-4 h-4" />
                        </div>
                        <span className="text-sm text-zinc-700">{feature.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2 px-2">
                    Portal del Empleado
                  </h4>
                  <div className="space-y-1">
                    {employeeFeatures.slice(0, 3).map((feature) => (
                      <Link
                        key={feature.name}
                        href={feature.href}
                        className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-zinc-100 transition-colors"
                        onClick={() => {
                          setOpen(false)
                          setMobileDropdownOpen(false)
                        }}
                      >
                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", feature.color)}>
                          <feature.icon className="w-4 h-4" />
                        </div>
                        <span className="text-sm text-zinc-700">{feature.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2 px-2">
                    Por tipo de negocio
                  </h4>
                  <div className="space-y-1">
                    {sectorFeatures.map((feature) => (
                      <Link
                        key={feature.name}
                        href={feature.href}
                        className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-zinc-100 transition-colors"
                        onClick={() => {
                          setOpen(false)
                          setMobileDropdownOpen(false)
                        }}
                      >
                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", feature.color)}>
                          <feature.icon className="w-4 h-4" />
                        </div>
                        <span className="text-sm text-zinc-700">{feature.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Link
              href="/empleados"
              className="px-4 py-3 text-sm font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              onClick={() => setOpen(false)}
            >
              Empleados
            </Link>
            <Link
              href="/restaurantes"
              className="px-4 py-3 text-sm font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              onClick={() => setOpen(false)}
            >
              Guía de restaurantes
            </Link>
            <Link
              href={getHref("precios")}
              className="px-4 py-3 text-sm font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              onClick={() => scrollTo("precios")}
            >
              Precios
            </Link>
            <div className="flex flex-col gap-2 pt-4 border-t border-zinc-200 mt-2">
              <Link
                href="/login"
                className="px-4 py-3 text-sm font-medium text-zinc-900 text-center border border-zinc-200 rounded-lg hover:bg-zinc-50"
              >
                Iniciar Sesión
              </Link>
              <Link
                href="/register"
                className="px-4 py-3 text-sm font-medium text-white text-center bg-primary hover:bg-[var(--primary-hover)] rounded-lg flex items-center justify-center gap-2"
              >
                Comenzar Gratis
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  )
}
